import React, { useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { Loading } from '../../components';
import { Badge, PageHeader, PageLayout, Segmented, StatePanel } from '../../components/ui';
import { UserContext } from '../../contexts';
import { WINDOWS, audienceOf, fillDays, fmtSeconds, fmtWhen, insightsFor, median, pageLabel, typeLabel, verdictFor } from './usageInsights';

import './usage-analytics.scss';

// One user's usage report: verdict, headline numbers, plain-language findings
// and the charts behind them. Master admins only.
// Backend: GET /api/admin/usage-analytics/users/{id} (+ the list report for the
// team / customer medians it is compared against).

const fmt = (n) => (n == null ? '—' : Number(n).toLocaleString('en-GB'));
const pct = (part, whole) => (whole ? Math.round((part * 100) / whole) : 0);

const FINDING_GROUPS = [
  ['usage', 'Usage'],
  ['limits', 'Rate limits'],
  ['automation', 'Browser or script'],
  ['pattern', 'When'],
  ['breadth', 'What'],
];

function ChartTooltip({ active, payload, label, unit = 'calls' }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="ua-tip">
      <span>{label}</span>
      <strong>{fmt(payload[0].value)} {unit}</strong>
    </div>
  );
}

function ChartCard({ title, note, children, wide = false }) {
  return (
    <section className={`ua-card${wide ? ' ua-card--wide' : ''}`}>
      <header className="ua-card__head">
        <h3>{title}</h3>
        {note && <span>{note}</span>}
      </header>
      {children}
    </section>
  );
}

const axisProps = { tick: { fill: 'currentColor', fontSize: 11 }, tickLine: false, axisLine: false };

export default function UsageUserReportPage() {
  const { user } = useContext(UserContext);
  const { id } = useParams();
  const history = useHistory();
  const location = useLocation();
  const initialDays = new URLSearchParams(location.search).get('days');
  const [days, setDays] = useState(['1', '7', '30'].includes(initialDays) ? initialDays : '7');
  const [detail, setDetail] = useState(null);
  const [peers, setPeers] = useState(null);
  const [row, setRow] = useState(null);
  const [error, setError] = useState('');

  const allowed = !!user?.is_master_admin;

  useEffect(() => {
    if (!allowed) return undefined;
    let live = true;
    setDetail(null);
    setError('');
    axios.get(`/api/admin/usage-analytics/users/${id}`, { params: { days } })
      .then((res) => live && setDetail(res.data))
      .catch((e) => live && setError(e.response?.data?.message || 'Could not load this user\'s report.'));
    // Medians for the comparison line; the list report is cached server-side.
    axios.get('/api/admin/usage-analytics', { params: { days } })
      .then((res) => {
        if (!live) return;
        const users = res.data?.users || [];
        setRow(users.find((u) => String(u.user_id) === String(id)) || null);
        const med = (a) => median(users.filter((u) => audienceOf(u) === a).map((u) => u.calls_per_day));
        setPeers({ team: med('team'), merchant: med('merchant'), client: med('client') });
      })
      .catch(() => live && setPeers(null));
    return () => { live = false; };
  }, [id, days, allowed]);

  const changeDays = (value) => {
    setDays(value);
    history.replace(`/usage-analytics/users/${id}?days=${value}`);
  };

  const daily = useMemo(() => fillDays(detail?.daily, Number(days)), [detail, days]);
  const hours = useMemo(() => Array.from({ length: 24 }, (_, h) => ({
    label: `${String(h).padStart(2, '0')}h`,
    calls: detail?.hour_of_day?.find((x) => x.hour === h)?.calls || 0,
  })), [detail]);

  if (!user) return <Loading />;
  if (!allowed) return <PageLayout><StatePanel state="permission" headingLevel={2} /></PageLayout>;

  // An older backend has no summary block — fall back to the list-report row,
  // which carries the same headline numbers but no outcome split.
  const s = detail?.summary || (detail && row ? row : null);
  const hasOutcomes = s?.ok_calls != null;
  const limits = detail?.limits || {};
  const verdict = s ? verdictFor(s, limits) : null;
  const findings = detail ? insightsFor({ ...detail, fallbackSummary: s }, peers) : null;
  const near = limits.near_limit_pct ?? 80;
  const peakPct = Math.min(100, s?.peak_pct_of_limit ?? 0);
  const outcomes = s ? [
    { key: 'ok', label: 'Succeeded', value: s.ok_calls, tone: 'success' },
    { key: 'client', label: 'Refused (4xx)', value: s.client_errors, tone: 'warning' },
    { key: 'blocked', label: 'Rate-limited (429)', value: s.throttled, tone: 'danger' },
    { key: 'server', label: 'Server error (5xx)', value: s.server_errors, tone: 'neutral' },
  ] : [];
  const topFeatures = (detail?.features || []).slice(0, 10);

  return (
    <PageLayout className="ua-page ua-report">
      <PageHeader
        title={detail?.user?.name || `User #${id}`}
        description={[detail?.user?.email, detail?.user?.store_name, typeLabel(detail?.user)].filter(Boolean).join(' · ')}
        backTo={`/usage-analytics?days=${days}`}
        actions={[]}
      />

      <div className="ua-controls">
        <Segmented label="Window" size="sm" options={WINDOWS} value={days} onChange={changeDays} />
        {s?.last_seen && (
          <span className="ua-muted">
            First seen {fmtWhen(s.first_seen)} · last API request {fmtWhen(s.last_seen)}
            {detail?.user?.last_page && <> · last page {pageLabel(detail.user.last_page)} ({detail.user.last_page})</>}
            {' · '}last OTP login {detail?.user?.last_otp_login_at ? fmtWhen(detail.user.last_otp_login_at) : 'none on record'}
          </span>
        )}
      </div>

      {error && <StatePanel state="error" title={error} headingLevel={2} />}
      {!error && !detail && <Loading />}

      {detail && (!s || s.calls === 0) && (
        <p className="ua-empty">No API calls from this user in this window.</p>
      )}

      {detail && s && s.calls > 0 && (
        <>
          <div className={`ua-verdict ua-verdict--${verdict.tone}`} role="status">
            <Badge tone={verdict.tone}>{verdict.label}</Badge>
            <span>{verdict.reason}</span>
          </div>

          <div className="ua-tiles ua-tiles--six">
            <div className="ua-tile"><span>API calls</span><strong>{fmt(s.calls)}</strong><em>{s.active_days} active day{s.active_days === 1 ? '' : 's'}</em></div>
            <div className="ua-tile"><span>Calls / active day</span><strong>{fmt(s.active_days ? Math.round(s.calls / s.active_days) : 0)}</strong>
              <em>{peers ? `median ${typeLabel({ ...detail.user, user_type: audienceOf(detail.user) }).toLowerCase()}: ${fmt(Math.round(peers[audienceOf(detail.user)] || 0))}` : ' '}</em></div>
            <div className="ua-tile">
              <span>Busiest minute</span>
              <strong>{fmt(s.peak_per_minute)} <small>/ {fmt(limits.api_per_minute_user)}</small></strong>
              <span className="ua-meter" role="meter" aria-valuemin={0} aria-valuemax={100} aria-valuenow={peakPct} aria-label="Busiest minute as a share of the limit">
                <i className={peakPct >= near ? 'is-hot' : undefined} style={{ width: `${peakPct}%` }} />
                <b style={{ left: `${near}%` }} title={`Alert at ${near}%`} />
              </span>
              <em>{s.peak_pct_of_limit ?? 0}% of limit · alert at {near}%</em>
            </div>
            <div className="ua-tile"><span>Records viewed</span><strong>{fmt(s.records_viewed)}</strong><em>{s.features_used} features used</em></div>
            <div className="ua-tile"><span>From scripts</span><strong>{pct(s.script_calls, s.calls)}%</strong><em>{fmt(s.script_calls)} calls</em></div>
            <div className="ua-tile"><span>Blocked (429)</span><strong>{fmt(s.throttled)}</strong><em>{fmtSeconds(s.server_seconds)} server time</em></div>
          </div>

          <section className="ua-findings" aria-labelledby="ua-findings-title">
            <h2 id="ua-findings-title" className="ua-subhead">Key findings</h2>
            <div className="ua-findings__grid">
              {FINDING_GROUPS.filter(([k]) => findings[k].length).map(([k, label]) => (
                <div key={k} className="ua-findings__group">
                  <h4>{label}</h4>
                  <ul>{findings[k].map((line) => <li key={line}>{line}</li>)}</ul>
                </div>
              ))}
            </div>
          </section>

          <div className="ua-charts">
            <ChartCard title="Calls per day" note="UTC" wide>
              <div className="ua-chart">
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={daily} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                    <CartesianGrid vertical={false} stroke="var(--border-hairline)" />
                    <XAxis dataKey="label" {...axisProps} interval="preserveStartEnd" minTickGap={16} />
                    <YAxis {...axisProps} width={44} allowDecimals={false} />
                    <Tooltip content={<ChartTooltip />} cursor={{ fill: 'var(--surface-wash)' }} />
                    <Bar dataKey="calls" fill="var(--accent)" radius={[4, 4, 0, 0]} maxBarSize={36} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </ChartCard>

            <ChartCard title="Hour of day" note={`UTC · active in ${s.active_hours_of_day} of 24 hours`}>
              <div className="ua-chart">
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={hours} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                    <CartesianGrid vertical={false} stroke="var(--border-hairline)" />
                    <XAxis dataKey="label" {...axisProps} interval={5} />
                    <YAxis {...axisProps} width={44} allowDecimals={false} />
                    <Tooltip content={<ChartTooltip />} cursor={{ fill: 'var(--surface-wash)' }} />
                    <Bar dataKey="calls" fill="var(--accent)" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </ChartCard>

            <ChartCard title="Features" note="calls per feature">
              <div className="ua-chart">
                <ResponsiveContainer width="100%" height={Math.max(120, topFeatures.length * 26 + 16)}>
                  <BarChart data={topFeatures} layout="vertical" margin={{ top: 0, right: 16, bottom: 0, left: 0 }}>
                    <XAxis type="number" hide />
                    <YAxis type="category" dataKey="feature" {...axisProps} width={120} />
                    <Tooltip content={<ChartTooltip />} cursor={{ fill: 'var(--surface-wash)' }} />
                    <Bar dataKey="calls" fill="var(--accent)" radius={[0, 4, 4, 0]} maxBarSize={16} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </ChartCard>

            {hasOutcomes && (
            <ChartCard title="Request outcomes">
              <div className="ua-stack" role="img" aria-label={outcomes.map((o) => `${o.label} ${o.value}`).join(', ')}>
                {outcomes.filter((o) => o.value > 0).map((o) => (
                  <i key={o.key} className={`ua-stack__seg ua-tone--${o.tone}`} style={{ flexGrow: o.value }} title={`${o.label}: ${fmt(o.value)}`} />
                ))}
              </div>
              <ul className="ua-legend">
                {outcomes.map((o) => (
                  <li key={o.key}><i className={`ua-tone--${o.tone}`} />{o.label}<b>{fmt(o.value)}</b><span>{pct(o.value, s.calls)}%</span></li>
                ))}
              </ul>
              <p className="ua-muted">{pct(s.write_calls, s.calls)}% of calls change data · average response {fmt(s.avg_ms)} ms</p>
            </ChartCard>
            )}
          </div>

          <div className="ua-charts">
            <ChartCard title="Top endpoints" wide>
              <div className="ua-scroll">
                <table className="ua-table ua-table--compact">
                  <thead><tr><th>Endpoint</th><th className="ua-num">Calls</th><th className="ua-num">Share</th><th className="ua-num">429s</th></tr></thead>
                  <tbody>
                    {detail.endpoints.slice(0, 15).map((e) => (
                      <tr key={`${e.method} ${e.path}`}>
                        <td><code>{e.method} /{e.path}</code></td>
                        <td className="ua-num">{fmt(e.calls)}</td>
                        <td className="ua-num">{pct(e.calls, s.calls)}%</td>
                        <td className="ua-num">{e.throttled ? <Badge tone="danger" size="sm">{e.throttled}</Badge> : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </ChartCard>

            <ChartCard title="Records opened" note="distinct records by type">
              {(detail.records_by_type || []).length === 0 ? <p className="ua-muted">No individual records opened.</p> : (
                <ul className="ua-kv">
                  {detail.records_by_type.map((r) => <li key={r.type}><span>{r.type}</span><b>{fmt(r.records)}</b><em>{fmt(r.calls)} calls</em></li>)}
                </ul>
              )}
            </ChartCard>

            <ChartCard title="Busiest minutes" note={`limit ${fmt(limits.api_per_minute_user)}/min`}>
              <ul className="ua-kv">
                {detail.busiest_minutes.map((m) => <li key={m.minute}><span>{fmtWhen(m.minute.replace(' ', 'T') + ':00Z')}</span><b>{fmt(m.calls)}</b><em>{pct(m.calls, limits.api_per_minute_user)}%</em></li>)}
              </ul>
            </ChartCard>

            <ChartCard title="Browsers and scripts" note="user agents">
              <ul className="ua-kv ua-kv--ua">
                {detail.user_agents.map((u) => (
                  <li key={u.user_agent || 'none'}>
                    <span>{u.is_script && <Badge tone="info" size="sm">Script</Badge>} <code>{u.user_agent || '(none)'}</code></span>
                    <b>{fmt(u.calls)}</b>
                    <em>{pct(u.calls, s.calls)}%</em>
                  </li>
                ))}
              </ul>
            </ChartCard>

            {(detail.rate_limit_events || []).length > 0 && (
              <ChartCard title="Rate-limit blocks" note="latest 20">
                <ul className="ua-kv">
                  {detail.rate_limit_events.map((ev) => <li key={ev.at}><span>{fmtWhen(ev.at)}</span><b><code>/{ev.path}</code></b><em>{fmt(ev.limit_per_minute)}/min</em></li>)}
                </ul>
              </ChartCard>
            )}
          </div>
        </>
      )}
    </PageLayout>
  );
}
